/**
 * EditState + decoded source → canvas. The only place pixels are touched.
 *
 * ONE transform, ONE resample (owner, 2026-09-06: "we cannot compromise on quality"). Every op —
 * crop, resize, rotate, flip — is an affine map, so the whole ordered list folds into a single
 * source→output matrix. The output is drawn from the ORIGINAL pixels in one drawImage: crops are clip
 * paths in their own space, corner fills are painted behind, flips and quarter turns are exact. When
 * the reduction is large the source is first downscaled ONCE with the browser's high-quality
 * resampler — to exactly the final scale when nothing rotates (so the final draw is 1:1), else to at
 * most 2× the final scale so the bilinear rotate never aliases.
 *
 * Previews run the same path at the SCREEN's resolution for the current zoom (device pixels), so a
 * photo shrunk to 277 px still previews sharply from the original instead of being a 100 px canvas
 * stretched across a Retina screen. Pre-downscaled copies live in a per-photo MipCache.
 */
import { EditState, Size, Op, cropRectPx, isIdentity, walk } from './edit-stack';

export type Source = HTMLCanvasElement | ImageBitmap | HTMLImageElement;
/** Downscaled copies of a source keyed by their scale (1/2, 1/4, … or an exact export scale). */
export type MipCache = Map<number, Source>;

const OFFSCREEN = typeof OffscreenCanvas !== 'undefined';

export function makeCanvas(size: Size): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(size.width)); c.height = Math.max(1, Math.round(size.height));
    return c;
}

function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = c.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('This browser could not create a drawing surface for the image.');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    return ctx;
}

const dims = (src: Source): Size => ({ width: (src as any).naturalWidth || src.width, height: (src as any).naturalHeight || src.height });

/** Stepwise high-quality scale of a source to an exact size (thumbnails; the fallback resampler). */
export async function scaleTo(src: Source, target: Size): Promise<HTMLCanvasElement> {
    const sw = (src as any).naturalWidth || src.width, sh = (src as any).naturalHeight || src.height;
    if (sw === target.width && sh === target.height) { const c = makeCanvas(target); ctx2d(c).drawImage(src as any, 0, 0); return c; }
    if (typeof createImageBitmap === 'function' && target.width < sw) {
        try {
            const bmp = await createImageBitmap(src as any, { resizeWidth: target.width, resizeHeight: target.height, resizeQuality: 'high' });
            const c = makeCanvas(target); ctx2d(c).drawImage(bmp, 0, 0); bmp.close?.(); return c;
        } catch { /* fall through to stepwise */ }
    }
    let cur: Source = src, cw = sw, ch = sh;
    while (cw / 2 >= target.width && ch / 2 >= target.height) {
        const half = makeCanvas({ width: Math.round(cw / 2), height: Math.round(ch / 2) });
        ctx2d(half).drawImage(cur as any, 0, 0, half.width, half.height);
        cur = half; cw = half.width; ch = half.height;
    }
    const out = makeCanvas(target); ctx2d(out).drawImage(cur as any, 0, 0, target.width, target.height);
    return out;
}

// ---------------------------------------------------------------- affine maps (canvas order: x' = a·x + c·y + e, y' = b·x + d·y + f)

export type Affine = [number, number, number, number, number, number];
const ID: Affine = [1, 0, 0, 1, 0, 0];
/** A ∘ B: apply B first, then A. */
export function mul(A: Affine, B: Affine): Affine {
    return [A[0] * B[0] + A[2] * B[1], A[1] * B[0] + A[3] * B[1], A[0] * B[2] + A[2] * B[3], A[1] * B[2] + A[3] * B[3], A[0] * B[4] + A[2] * B[5] + A[4], A[1] * B[4] + A[3] * B[5] + A[5]];
}
const translate = (x: number, y: number): Affine => [1, 0, 0, 1, x, y];
const scaleM = (x: number, y = x): Affine => [x, 0, 0, y, 0, 0];
const rotateM = (rad: number): Affine => [Math.cos(rad), Math.sin(rad), -Math.sin(rad), Math.cos(rad), 0, 0];
export function apply(m: Affine, x: number, y: number): [number, number] { return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]; }

/** One op as an affine map from its input pixels to its output pixels. */
function opAffine(op: Op, inPx: Size, outPx: Size): Affine {
    switch (op.type) {
        case 'crop': { const r = cropRectPx(op, inPx); const t = translate(-r.x, -r.y); return op.platform ? mul(scaleM(outPx.width / r.width, outPx.height / r.height), t) : t; }
        case 'resize': return scaleM(outPx.width / inPx.width, outPx.height / inPx.height);
        case 'rotate': return mul(translate(outPx.width / 2, outPx.height / 2), mul(rotateM((op.quarterTurns * 90 + op.fineAngle) * Math.PI / 180), translate(-inPx.width / 2, -inPx.height / 2)));
        case 'flip': return mul(translate(op.h ? inPx.width : 0, op.v ? inPx.height : 0), scaleM(op.h ? -1 : 1, op.v ? -1 : 1));
    }
}

export interface Step { op: Op; inPx: Size; outPx: Size; post: Affine; }   // post: this op's OUTPUT space → the final output
export interface Composite { m: Affine; out: Size; steps: Step[]; rotates: boolean; }

/** The first `upTo` ops folded into one source→output matrix, plus each step's post-transform (clips, fills). */
export function composite(s: EditState, upTo = s.ops.length): Composite {
    const locals: Affine[] = []; const steps: Step[] = []; let inPx: Size = { ...s.source }; let rotates = false;
    // sizes come from the model's walk (cumulative-scale aware), so the transform lands on the model's numbers
    for (const st of walk(s, upTo)) {
        const { op, outPx } = st;
        if (!isIdentity(op)) {
            locals.push(opAffine(op, st.inPx, outPx)); steps.push({ op, inPx: st.inPx, outPx, post: ID });
            if (op.type === 'rotate' && op.fineAngle !== 0) rotates = true;
        }
        inPx = outPx;
    }
    let m = ID; for (const l of locals) m = mul(l, m);
    let post: Affine = ID;
    for (let i = steps.length - 1; i >= 0; i--) { steps[i].post = post; post = mul(post, locals[i]); }
    return { m, out: inPx, steps, rotates };
}

/** A copy of the source at scale `p` (≤ 1), built with the browser's high-quality resampler and cached. */
async function level(src: Source, p: number, mips?: MipCache): Promise<Source> {
    if (p >= 1) return src;
    const hit = mips?.get(p); if (hit) return hit;
    const d = dims(src); const target = { width: Math.max(1, Math.round(d.width * p)), height: Math.max(1, Math.round(d.height * p)) };
    let out: Source | null = null;
    if (typeof createImageBitmap === 'function') { try { out = await createImageBitmap(src as any, { resizeWidth: target.width, resizeHeight: target.height, resizeQuality: 'high' }); } catch { out = null; } }
    if (!out) out = await scaleTo(src, target);
    mips?.set(p, out); return out;
}

export interface RenderOptions {
    scale?: number;      // canvas px per output px (1 = export; previews pass zoom × devicePixelRatio)
    maxEdge?: number;    // cap on the canvas's longer edge
    upTo?: number;       // render only the first n ops (the Crop tool's base)
    mips?: MipCache;     // per-photo cache of downscaled copies
    exact?: boolean;     // export: pre-scale to exactly the final scale (one true resample) instead of a cached power of two
}

/** The composed edit, drawn from the original in one pass. */
export async function render(src: Source, s: EditState, o: RenderOptions = {}): Promise<HTMLCanvasElement> {
    const C = composite(s, o.upTo);
    let k = o.scale ?? 1;
    if (o.maxEdge) k = Math.min(k, o.maxEdge / Math.max(C.out.width, C.out.height, 1));
    const lin = Math.sqrt(Math.abs(C.m[0] * C.m[3] - C.m[1] * C.m[2]) || 1);   // linear scale source → output
    if (lin * k > 1) k = 1 / lin;                                                // never more pixels than the source has
    const s2 = lin * k;                                                          // linear scale source → canvas
    // pre-downscale: exactly s2 when nothing rotates (export) → the final draw is 1:1; else the largest
    // power of two that leaves the final reduction ≤ 2× (cached across zooms)
    const p = s2 >= 0.999 ? 1 : (o.exact && !C.rotates) ? s2 : Math.max(1 / 64, Math.pow(2, Math.floor(Math.log2(Math.min(1, 2 * s2)))));
    const img = await level(src, p, o.mips);
    const pReal = dims(img).width / dims(src).width;
    const canvas = makeCanvas({ width: Math.round(C.out.width * k), height: Math.round(C.out.height * k) });
    const ctx = ctx2d(canvas);
    const K = scaleM(k);
    const set = (T: Affine) => ctx.setTransform(T[0], T[1], T[2], T[3], T[4], T[5]);
    // every crop is a window in its own output space (a disc for circle avatars); clips intersect
    for (const st of C.steps) if (st.op.type === 'crop') {
        set(mul(K, st.post)); ctx.beginPath();
        if (st.op.shape === 'circle') ctx.ellipse(st.outPx.width / 2, st.outPx.height / 2, st.outPx.width / 2, st.outPx.height / 2, 0, 0, Math.PI * 2);
        else ctx.rect(0, 0, st.outPx.width, st.outPx.height);
        ctx.clip();
    }
    // corner fills behind a fine-angle rotation that kept its bounds
    for (const st of C.steps) if (st.op.type === 'rotate' && st.op.fineAngle !== 0 && st.op.cornerFill !== 'transparent' && st.op.cornerFill !== 'auto-crop') {
        set(mul(K, st.post)); ctx.fillStyle = st.op.cornerFill; ctx.fillRect(0, 0, st.outPx.width, st.outPx.height);
    }
    // the photo, once: level px → source px (÷pReal) → output px (C.m) → canvas px (×k)
    set(mul(K, mul(C.m, scaleM(1 / pReal))));
    ctx.drawImage(img as any, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return canvas;
}

/** Full-size output for export: one high-quality resample from the original. */
export async function renderOutput(src: Source, s: EditState, mips?: MipCache): Promise<HTMLCanvasElement> { return render(src, s, { scale: 1, mips, exact: true }); }

/**
 * Preview at the screen's resolution: `displayScale` = zoom × devicePixelRatio (device px per image
 * px), capped by `maxEdge` and by the source's own resolution. `upTo` renders only the first ops —
 * the Crop tool edits the image the ops before it produced.
 */
export async function renderPreview(src: Source, s: EditState, o: { maxEdge: number; displayScale?: number; upTo?: number; mips?: MipCache }): Promise<{ canvas: HTMLCanvasElement; scale: number }> {
    const canvas = await render(src, s, { scale: o.displayScale ?? 1, maxEdge: o.maxEdge, upTo: o.upTo, mips: o.mips });
    const out = composite(s, o.upTo).out;
    return { canvas, scale: canvas.width / Math.max(1, out.width) };
}

/** Rough byte estimate for the output line before the real encode runs (calibrated on typical photos). */
export function estimateBytes(size: Size, format: string, quality = 85, hasAlpha = false): number {
    const px = size.width * size.height;
    switch (format) {
        case 'jpg': return Math.round(px * (0.05 + 0.25 * Math.pow(quality / 100, 3)));
        case 'webp': return Math.round(px * (0.035 + 0.18 * Math.pow(quality / 100, 3)));
        case 'avif': return Math.round(px * (0.02 + 0.12 * Math.pow(quality / 100, 3)));
        case 'png': return Math.round(px * (hasAlpha ? 1.4 : 1.1));
        case 'gif': return Math.round(px * 0.5);
        case 'bmp': return px * 3 + 54;
        case 'tiff': return px * (hasAlpha ? 4 : 3) + 1024;
        case 'ppm': return px * 3 + 20;
        case 'psd': return px * (hasAlpha ? 4 : 3) + 4096;
        case 'ico': return Math.round(px * 1.2);
        default: return Math.round(px * 0.3);
    }
}

export function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

export function isOffscreenSupported(): boolean { return OFFSCREEN; }
